import React from 'react';
import { LegalPageLayout } from './LegalPageLayout';

const Section = ({
  number,
  title,
  children,
}: {
  number?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section className="p-6 md:p-8 rounded-3xl bg-zinc-900/40 border border-white/5 backdrop-blur-sm">
    {number && (
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-lg bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-400 font-bold text-sm">
          {number}
        </span>
        <h2 className="text-white font-bold text-xl md:text-2xl">{title}</h2>
      </div>
    )}
    {!number && <h2 className="text-white font-bold text-xl md:text-2xl mb-4">{title}</h2>}
    <div className="text-zinc-400 leading-relaxed text-base md:text-lg space-y-4">{children}</div>
  </section>
);

const WarningBox = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`p-5 rounded-2xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20 backdrop-blur-sm ${className}`}
  >
    {children}
  </div>
);

export const CommunityGuidelinesPage: React.FC = () => {
  return (
    <LegalPageLayout title="Community Guidelines" lastUpdated="April 15, 2026" icon="users">
      <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm mb-8">
        <h2 className="text-white font-bold text-2xl md:text-3xl mb-4">
          Building a Better Community
        </h2>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg">
          Musicade brings together a global community of creators, collaborators, and audiences to
          discover and share live experiences. We are committed to maintaining a safe, trustworthy,
          and high-quality platform.
        </p>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg mt-4">
          These Community Guidelines outline what is not allowed on Musicade or in connection with
          the use of our services. They form part of the broader Terms of Service.
        </p>
      </section>

      <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 backdrop-blur-sm mb-8">
        <h2 className="text-white font-bold text-xl md:text-2xl mb-4">Our Core Principles</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-purple-400 block mb-1">Authenticity</strong>
            <span className="text-zinc-400 text-sm">Real events, real creators</span>
          </div>
          <div className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-purple-400 block mb-1">Safety</strong>
            <span className="text-zinc-400 text-sm">Physical and digital protection</span>
          </div>
          <div className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-purple-400 block mb-1">Trust</strong>
            <span className="text-zinc-400 text-sm">Accurate listings and fair transactions</span>
          </div>
          <div className="p-4 rounded-xl bg-zinc-800/30 border border-white/5">
            <strong className="text-purple-400 block mb-1">Respect</strong>
            <span className="text-zinc-400 text-sm">Inclusive and professional interactions</span>
          </div>
        </div>
      </section>

      <h2 className="text-white font-bold text-2xl md:text-3xl mb-6 px-2">
        Prohibited Content & Activities
      </h2>

      <WarningBox className="mb-6">
        <p className="text-red-300">
          <strong className="text-white">Zero Tolerance:</strong> The following activities will
          result in immediate account termination and may be reported to authorities.
        </p>
      </WarningBox>

      <div className="space-y-4">
        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              1
            </span>
            <h3 className="text-white font-bold text-lg">Fraud & Misrepresentation</h3>
          </div>
          <p className="text-zinc-400">
            We prohibit any activity that deceives users or undermines trust, including fake events,
            misrepresenting details, or impersonating individuals.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              2
            </span>
            <h3 className="text-white font-bold text-lg">Platform & Payment Abuse</h3>
          </div>
          <p className="text-zinc-400">
            Bypassing Musicade's payment system or manipulating pricing is strictly prohibited.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              3
            </span>
            <h3 className="text-white font-bold text-lg">Spam & Manipulation</h3>
          </div>
          <p className="text-zinc-400">
            We prohibit duplicate listings, fake engagement, or unsolicited communications designed
            to exploit the platform.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              4
            </span>
            <h3 className="text-white font-bold text-lg">Harmful Conduct & Bullying</h3>
          </div>
          <p className="text-zinc-400">
            We do not tolerate abuse, harassment, stalking, or encouraging dangerous behavior.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              5
            </span>
            <h3 className="text-white font-bold text-lg">Hate Speech & Discrimination</h3>
          </div>
          <p className="text-zinc-400">
            We prohibit attacks based on race, gender, religion, nationality, sexual orientation, or
            disability.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              6
            </span>
            <h3 className="text-white font-bold text-lg">Violence & Physical Harm</h3>
          </div>
          <p className="text-zinc-400">
            Events or content that promote or enable physical harm or lack proper safety planning
            are not allowed.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              7
            </span>
            <h3 className="text-white font-bold text-lg">Illegal Activities</h3>
          </div>
          <p className="text-zinc-400">
            Using Musicade for illegal purposes, including drug-related activities or fraud schemes,
            is prohibited.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              8
            </span>
            <h3 className="text-white font-bold text-lg">Sexual Content & Exploitation</h3>
          </div>
          <p className="text-zinc-400">
            We prohibit explicit or exploitative content, including pornography and sexual services.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              9
            </span>
            <h3 className="text-white font-bold text-lg">Child Safety</h3>
          </div>
          <p className="text-zinc-400">
            Any content involving exploitation of minors or collecting data from minors without
            consent will result in immediate action.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              10
            </span>
            <h3 className="text-white font-bold text-lg">Privacy Violations</h3>
          </div>
          <p className="text-zinc-400">
            Respecting user privacy is mandatory. Sharing personal data without consent is
            prohibited.
          </p>
        </section>

        <section className="p-6 rounded-3xl bg-zinc-900/40 border border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm">
              11
            </span>
            <h3 className="text-white font-bold text-lg">Intellectual Property</h3>
          </div>
          <p className="text-zinc-400">
            You must have rights to all content you upload, including images, videos, and logos.
          </p>
        </section>
      </div>

      <Section title="Enforcement">
        <p>
          We enforce these Guidelines based on severity and risk. Actions may include content
          removal, event cancellation, feature restrictions, or permanent bans. Serious violations
          may result in immediate account termination and reporting to authorities.
        </p>
      </Section>

      <Section title="Reporting Violations">
        <p>
          If you encounter content or behavior that may violate these Guidelines, please use the
          Report feature on events or profiles, or contact support directly at{' '}
          <a href="mailto:support@musicade.com" className="text-blue-400 hover:text-blue-300">
            support@musicade.com
          </a>
          .
        </p>
      </Section>
    </LegalPageLayout>
  );
};
